import React, { useEffect, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast'; // Usando o toast que configuramos
import { Save, Globe, ShoppingBag, ShoppingCart, Package, Edit2, MessageCircle, RefreshCw, Layout, ShieldCheck, MessageSquare, Send, Store, Handshake, LogOut } from 'lucide-react';
import io from 'socket.io-client';
import { QRCodeCanvas } from 'qrcode.react';
import { BASE_URL } from '../config';
import { useConfirm } from '../context/ConfirmContext';

const socket = io(BASE_URL, {
    transports: ['websocket', 'polling']
}); // Conexão dinâmica baseada na porta do navegador

const InstagramIcon = ({ size, color }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
    </svg>
);

const TikTokIcon = ({ size = 20, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3 15.28a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.21 8.21 0 0 0 4.91 1.62v-3.5a4.87 4.87 0 0 1-1-.14z"/>
    </svg>
);

const ConfigPage = ({ userPlan = 'free', userRole = 'user', userId, username, onNavigateTab }) => {
    const confirm = useConfirm();
    const [isEditing, setIsEditing] = useState({
        shopeeAppId: false,
        shopeeSecret: false
    });

    // Função para mascarar os valores (ex: 12345 -> *****)
    const maskValue = (value) => {
        if (!value) return '';
        return '*'.repeat(value.length > 10 ? 10 : value.length);
    };

    const isAdmin = userRole === 'admin' || userRole === 'moderator';
    const isDivulgador = Boolean(userPlan && userPlan.endsWith('_divulgador'));
    const isPayingPlan = Boolean(userPlan && (userPlan.startsWith('basic') || userPlan.startsWith('premium')));
    const isPremiumPlan = Boolean(userPlan && (userPlan === 'premium' || userPlan.startsWith('premium_') || userPlan === 'admin'));

    const [config, setConfig] = useState({
        templateMensagem: '',
        horaInicio: 8,
        horaFim: 22,
        intervaloMinutos: 30,
        ambiente: 'dev'
    });
    const [mainTab, setMainTab] = useState((userPlan && userPlan.endsWith('_divulgador')) ? 'whatsapp' : 'plataformas');
    const [selectedPlatform, setSelectedPlatform] = useState('shopee');
    const [saving, setSaving] = useState(false);
    const [showManualInsta, setShowManualInsta] = useState(false);
    const [loadingInstaAuth, setLoadingInstaAuth] = useState(false);
    const [qrCodeMe, setQrCodeMe] = useState('');
    const [whatsStatusMe, setWhatsStatusMe] = useState('Iniciando...');

    useEffect(() => {
        if (isDivulgador && mainTab !== 'whatsapp' && mainTab !== 'telegram') {
            setMainTab('whatsapp');
        }
    }, [isDivulgador, mainTab]);

    useEffect(() => {
        // Busca configurações iniciais
        api.get('/config').then(res => setConfig(res.data));

        // Lógica para detectar se esta janela é um popup (após redirecionamento da Meta)
        const urlParams = new URLSearchParams(window.location.search);
        const instaStatus = urlParams.get('instagram_connected');
        
        if (instaStatus) {
            // Verifica se a janela tem um "pai" (foi aberta como popup via window.open)
            if (window.opener && window.opener !== window) {
                const errMsg = urlParams.get('error_message') || "Falha na autenticação.";
                
                // Dispara o aviso para a janela pai (principal)
                window.opener.postMessage({
                    type: 'INSTAGRAM_OAUTH',
                    status: instaStatus,
                    error: errMsg
                }, window.location.origin);
                
                // Fecha a janela logo após disparar o evento
                setTimeout(() => {
                    window.close();
                }, 100);
                
                // Retorna para evitar rodar outros toasts aqui dentro do popup
                return;
            } else {
                // Caso não seja popup (fallback)
                if (instaStatus === 'success') {
                    toast.success("Instagram conectado com sucesso!");
                } else if (instaStatus === 'error') {
                    const errMsg = urlParams.get('error_message') || "Falha na autenticação.";
                    toast.error(`Erro ao conectar Instagram: ${errMsg}`, { duration: 6000 });
                }
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        }

        // Escuta as mensagens vindas do popup
        const handleMessage = (event) => {
            // Removemos a trava estrita de event.origin !== window.location.origin
            // porque o popup vem da URL do backend (ex: ngrok), que é uma origem diferente.
            
            if (event.data?.type === 'INSTAGRAM_OAUTH') {
                if (event.data.status === 'success') {
                    toast.success("Instagram conectado com sucesso!");
                    // Recarrega as configurações para puxar o ID
                    api.get('/config').then(res => setConfig(res.data));
                } else if (event.data.status === 'error') {
                    toast.error(event.data.error || "Erro ao conectar conta.");
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const checkStatusMe = async () => {
        try {
            const res = await api.get('/config/whatsapp/me');
            if (res.data.connected) {
                setWhatsStatusMe('Conectado');
                setQrCodeMe('');
            } else if (res.data.state === 'INITIALIZING') {
                setWhatsStatusMe('Iniciando...');
                setQrCodeMe('');
            } else {
                if (res.data.qrCode) {
                    setQrCodeMe(res.data.qrCode);
                    setWhatsStatusMe('Aguardando leitura');
                } else {
                    setWhatsStatusMe('Desconectado');
                    setQrCodeMe('');
                }
            }
        } catch (err) {
            setWhatsStatusMe('Desconectado');
            setQrCodeMe('');
        }
    };

    const handleLogoutMe = async () => {
        const ok = await confirm({
            title: "Desconectar WhatsApp",
            message: "Deseja realmente deslogar o seu WhatsApp? Para voltar a enviar mensagens e receber notificações, será necessário escanear o QR Code novamente.",
            confirmText: "Sim, deslogar",
            cancelText: "Cancelar",
            type: "danger"
        });
        if (!ok) return;

        const loadingToast = toast.loading("Desconectando WhatsApp...");
        try {
            await api.get('/config/whatsapp/me/logout');
            toast.success("WhatsApp desconectado com sucesso!", { id: loadingToast });
            setWhatsStatusMe('Iniciando...');
            setQrCodeMe('');
            setTimeout(() => {
                checkStatusMe();
            }, 2000);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao desconectar", { id: loadingToast });
        }
    };

    useEffect(() => {
        if (!userId) {
            return;
        }

        checkStatusMe();
        const intervalMe = setInterval(() => {
            checkStatusMe();
        }, 5000);

        socket.on(`qrCode_user_${userId}`, (qr) => {
            setQrCodeMe(qr);
            setWhatsStatusMe('Aguardando leitura');
        });

        socket.on(`auth_success_user_${userId}`, (isAuth) => {
            setWhatsStatusMe(isAuth ? 'Conectado' : 'Desconectado');
            if (isAuth) setQrCodeMe('');
        });

        return () => {
            clearInterval(intervalMe);
            socket.off(`qrCode_user_${userId}`);
            socket.off(`auth_success_user_${userId}`);
        };
    }, [userId, userPlan]);

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        const loadingToast = toast.loading("Salvando configurações...");
        try {
            await api.post('/config', config);
            toast.success("Configurações salvas com sucesso!", { id: loadingToast });
        } catch (err) {
            toast.error("Erro ao salvar configurações", { id: loadingToast });
        } finally {
            setSaving(false);
        }
    };

    const handleUnlinkTelegramGlobal = async () => {
        const loadingToast = toast.loading("Desvinculando Telegram...");
        try {
            await api.post('/config', { telegramGlobalChatId: null });
            setConfig(prev => ({
                ...prev,
                telegramGlobalChatId: null
            }));
            toast.success("Telegram desvinculado com sucesso!", { id: loadingToast });
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao desvincular", { id: loadingToast });
        }
    };

    const loadExamplePrompt = (promptKey) => {
        const exampleAdmin = `Você é a Mayara, uma assistente de compras ultra-eficiente.
Sua missão principal é converter imagens e pedidos em vendas.

REGRAS DE OURO (Siga com rigor):
1. PRIORIDADE VISUAL: Se houver uma imagem, ANALISE-A IMEDIATAMENTE. Nunca peça foto se uma imagem já existe no contexto.
2. COMANDO DE BUSCA: Responda obrigatoriamente começando com "BUSCA: [Nome Curto]".
- Use no máximo 4 palavras.
- Remova tamanhos (ex: 39), conjunções (ex: ou, para) e detalhes inúteis.
3. FILTRO DE ACESSÓRIOS: Se identificar um objeto, foque no objeto principal, não em peças de reposição ou capas.
4. ZERO PAPO FURADO: Se houver imagem, ignore o texto do usuário e responda apenas o comando BUSCA.
5. PROIBIÇÃO DE COLCHETES: Nunca use "[especificar modelo]". Escolha o modelo mais provável pela imagem.
6. MODO CONVERSA: Apenas se NÃO houver imagem, seja breve, gentil e tente vender.
7. Se o usuário encaminhar algum link de um produto ou perguntar sobre você, explique de forma amigável que você compartilha ofertas encontradas na internet e que para comprar basta adicionar o produto ao carrinho na Shopee/Mercado Livre.`;

        const exampleUser = `Você é um assistente de compras inteligente e ultra-eficiente.
Sua missão principal é converter imagens e pedidos em vendas.

ATENÇÃO: Atualmente, o sistema só retorna links de ofertas da Shopee.

REGRAS DE OURO (Siga com rigor):
1. PRIORIDADE VISUAL: Se houver uma imagem, ANALISE-A IMEDIATAMENTE. Nunca peça foto se uma imagem já existe no contexto.
2. COMANDO DE BUSCA: Responda obrigatoriamente começando com "BUSCA: [Nome Curto]".
- Use no máximo 4 palavras.
- Remova tamanhos, conjunções e detalhes inúteis.
3. FILTRO DE ACESSÓRIOS: Se identificar um objeto, foque no objeto principal, não em peças de reposição ou capas.
4. ZERO PAPO FURADO: Se houver imagem, ignore o texto do usuário e responda apenas o comando BUSCA.
5. PROIBIÇÃO DE COLCHETES: Nunca use "[especificar modelo]". Escolha o modelo mais provável pela imagem.
6. MODO CONVERSA: Apenas se NÃO houver imagem, seja breve, gentil e tente vender.
7. Se o usuário perguntar sobre você ou o produto, explique de forma educada que você é uma assistente virtual de buscas que encontra as melhores ofertas na Shopee e que para comprar basta clicar no link.`;

        const txt = (promptKey === 'aiPromptA' || promptKey === 'aiPromptB') ? exampleAdmin : exampleUser;
        setConfig({ ...config, [promptKey]: txt });
        toast.success("Modelo de prompt carregado! Não se esqueça de salvar.");
    };

    const renderWhatsappBox = (instance, label, status, qrCode, hideButtons = false) => {
        let activeKey = null;
        let promptKey = null;
        if (instance === 'A') {
            activeKey = 'whatsappAutoReplyA';
            promptKey = 'aiPromptA';
        } else if (instance === 'B') {
            activeKey = 'whatsappAutoReplyB';
            promptKey = 'aiPromptB';
        } else if (instance === 'me' && (isPremiumPlan || isAdmin)) {
            activeKey = 'whatsappAutoReply';
            promptKey = 'aiPrompt';
        }

        return (
            <div style={{ flex: 1, padding: '20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <h4 style={{ marginBottom: '5px', color: '#1e293b', fontWeight: 'bold', textAlign: 'center' }}>{label}</h4>
                
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    {status === 'Conectado' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                            <div style={{ fontSize: '40px' }}>✅</div>
                            <p style={{ fontWeight: 'bold', color: '#1e293b' }}>Conectado e Ativo</p>
                            {!hideButtons && (
                                <button 
                                    onClick={handleLogoutMe}
                                    style={{
                                        marginTop: '10px',
                                        backgroundColor: '#fef2f2',
                                        color: '#ef4444',
                                        border: '1px solid #fee2e2',
                                        padding: '9px 18px',
                                        borderRadius: '10px',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 1px 2px rgba(239, 68, 68, 0.05)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                        e.currentTarget.style.borderColor = '#fca5a5';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fef2f2';
                                        e.currentTarget.style.borderColor = '#fee2e2';
                                    }}
                                >
                                    <LogOut size={16} /> Deslogar WhatsApp
                                </button>
                            )}
                        </div>
                    ) : qrCode ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <QRCodeCanvas value={qrCode} size={200} />
                            <p style={{ fontSize: '12px', color: '#64748b' }}>Escaneie o QR Code acima para conectar.</p>
                        </div>
                    ) : status === 'Desconectado' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                            <div style={{ fontSize: '40px' }}>🔴</div>
                            <p style={{ fontWeight: 'bold', color: '#ef4444' }}>Desconectado</p>
                            <p style={{ fontSize: '12px', color: '#64748b' }}>A instância está reiniciando ou indisponível.</p>
                            {!hideButtons && (
                                <button 
                                    onClick={handleLogoutMe}
                                    style={{
                                        marginTop: '10px',
                                        background: '#ef4444',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <RefreshCw size={16} /> Forçar Novo QR Code
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', padding: '20px' }}>
                            <RefreshCw className="animate-spin" size={32} color="#64748b" />
                            <p style={{ color: '#64748b' }}>Aguardando motor do WhatsApp...</p>
                            {!hideButtons && (
                                <button 
                                    onClick={handleLogoutMe}
                                    style={{
                                        marginTop: '10px',
                                        background: '#ef4444',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <RefreshCw size={16} /> Reiniciar Motor
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {activeKey && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0', textAlign: 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div>
                                <strong style={{ fontSize: '13px', color: '#1e293b' }}>Auto-Resposta IA</strong>
                                <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>Responder conversas com IA</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setConfig({ ...config, [activeKey]: config[activeKey] !== false ? false : true })}
                                style={{
                                    width: '44px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    backgroundColor: config[activeKey] !== false ? '#25d366' : '#cbd5e1',
                                    border: 'none',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: '0.3s'
                                }}
                            >
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '4px',
                                    left: config[activeKey] !== false ? '24px' : '4px',
                                    transition: '0.3s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                            </button>
                        </div>
                        {(instance === 'A' || instance === 'me') && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px', marginBottom: '10px' }}>
                                <div>
                                    <strong style={{ fontSize: '13px', color: '#1e293b' }}>Postar no Status</strong>
                                    <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>Postar ofertas automaticamente no seu Status do WhatsApp</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setConfig({ ...config, whatsappStatusActive: !config.whatsappStatusActive })}
                                    style={{
                                        width: '44px',
                                        height: '24px',
                                        borderRadius: '12px',
                                        backgroundColor: config.whatsappStatusActive ? '#25d366' : '#cbd5e1',
                                        border: 'none',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '4px',
                                        left: config.whatsappStatusActive ? '24px' : '4px',
                                        transition: '0.3s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                </button>
                            </div>
                        )}
                        {config[activeKey] !== false && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Prompt do Sistema (Personalidade da IA)</label>
                                    <button
                                        type="button"
                                        onClick={() => loadExamplePrompt(promptKey)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#25d366',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            padding: '2px 5px',
                                            borderRadius: '4px',
                                            transition: '0.2s',
                                        }}
                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                        ✨ Carregar Exemplo
                                    </button>
                                </div>
                                <textarea
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '13px',
                                        fontFamily: 'monospace',
                                        minHeight: '120px',
                                        resize: 'vertical',
                                        outline: 'none',
                                        backgroundColor: '#fff',
                                        color: '#334155'
                                    }}
                                    placeholder={instance === 'me' 
                                        ? "Exemplo: Você é um assistente de compras inteligente. Atualmente, o sistema só retorna links de ofertas da Shopee...\n\nClique em 'Carregar Exemplo' acima para preencher o modelo!"
                                        : "Instruções para a IA... (deixe em branco para usar o padrão)\n\nClique em 'Carregar Exemplo' acima para preencher o modelo!"
                                    }
                                    value={config[promptKey] || ''}
                                    onChange={(e) => setConfig({ ...config, [promptKey]: e.target.value })}
                                />
                                {instance === 'me' && (
                                    <p style={{ fontSize: '10.5px', color: '#64748b', margin: '2px 0 0 0', fontStyle: 'italic' }}>
                                        * Atualmente, o seu assistente de auto-resposta no WhatsApp retornará links exclusivamente da Shopee.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };


    const gerarVerifyToken = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let token = 'anuncios_';
        for (let i = 0; i < 32; i++) {
            token += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setConfig(prev => ({ ...prev, instagramVerifyToken: token }));
        toast.success("Novo token gerado! Clique em SALVAR ALTERAÇÕES.");
    };

    const handleConnectInstagram = async () => {
        setLoadingInstaAuth(true);
        const loadToast = toast.loading("Gerando link seguro com o Instagram...");
        try {
            const res = await api.get('/instagram/auth-url');
            if (res.data && res.data.url) {
                toast.success("Abrindo janela de login do Facebook...", { id: loadToast });
                
                // Abre o popup centralizado
                const width = 600;
                const height = 700;
                const left = (window.innerWidth / 2) - (width / 2);
                const top = (window.innerHeight / 2) - (height / 2);
                
                window.open(
                    res.data.url, 
                    'MetaAuth', 
                    `width=${width},height=${height},top=${top},left=${left},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
                );
            } else {
                throw new Error("Não foi possível obter a URL de autorização.");
            }
        } catch (err) {
            const msg = err.response?.data?.error || "Erro ao conectar com o servidor Meta. Verifique as credenciais da Meta no arquivo .env do backend.";
            toast.error(msg, { id: loadToast });
        } finally {
            setLoadingInstaAuth(false);
        }
    };

    const copiarVerifyToken = () => {
        if (!config.instagramVerifyToken) {
            toast.error("Não há token para copiar!");
            return;
        }
        navigator.clipboard.writeText(config.instagramVerifyToken);
        toast.success("Token copiado com sucesso!");
    };

    return (
        <div className="page-wrapper">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <h1 style={{ margin: 0 }}>⚙️ Configurações do Sistema</h1>
                <button
                    id="btn-save-config-top"
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        padding: '10px 22px',
                        backgroundColor: '#25d366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 6px rgba(37, 211, 102, 0.3)',
                        transition: 'all 0.2s ease',
                        opacity: saving ? 0.8 : 1
                    }}
                >
                    {saving ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
                    {saving ? 'Salvando...' : 'Salvar Configurações'}
                </button>
            </div>

            {/* ABAS SUPERIORES */}
            <div className="responsive-tabs">
                {!isDivulgador && (
                    <TabBtn active={mainTab === 'plataformas'} onClick={() => setMainTab('plataformas')} icon={<Globe size={18} />} label="APIs" />
                )}
                <TabBtn active={mainTab === 'whatsapp'} onClick={() => setMainTab('whatsapp')} icon={<MessageCircle size={18} />} label="WhatsApp" />
                <TabBtn active={mainTab === 'telegram'} onClick={() => setMainTab('telegram')} icon={<Send size={18} />} label="Telegram" />
                {!isDivulgador && (userPlan !== 'free' || isAdmin) && (
                    <TabBtn active={mainTab === 'template'} onClick={() => setMainTab('template')} icon={<Layout size={18} />} label="Template" />
                )}
                {!isDivulgador && (userPlan === 'basic' || userPlan === 'premium' || isAdmin) && (
                    <TabBtn active={mainTab === 'vitrine'} onClick={() => setMainTab('vitrine')} icon={<Store size={18} />} label="Vitrine" />
                )}
            </div>

            <div className="page-container">
                {/* ABA PLATAFORMAS (Igual ao seu anterior) */}

                {mainTab === 'plataformas' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Seleção de Plataforma */}
                        <div className="platform-scroll-container">
                            <PlatformBtn active={selectedPlatform === 'shopee'} onClick={() => setSelectedPlatform('shopee')} color="#ff4d2d" icon={<ShoppingBag />} label="Shopee" />
                            <PlatformBtn active={selectedPlatform === 'ml'} onClick={() => setSelectedPlatform('ml')} color="#2d3277" icon={<Handshake />} label="M. Livre" locked={!(isPayingPlan || isAdmin)} />
                            <PlatformBtn active={selectedPlatform === 'magalu'} onClick={() => setSelectedPlatform('magalu')} color="#0086ff" icon={<Store />} label="Magalu" />
                            <PlatformBtn active={selectedPlatform === 'amazon'} onClick={() => setSelectedPlatform('amazon')} color="#ff9900" icon={<ShoppingCart />} label="Amazon" locked={!(isPayingPlan || isAdmin)} />
                            <PlatformBtn active={selectedPlatform === 'shein'} onClick={() => setSelectedPlatform('shein')} color="#222" icon={<Store />} label="Shein" locked={!(isPayingPlan || isAdmin)} />
                            <PlatformBtn active={selectedPlatform === 'aliexpress'} onClick={() => setSelectedPlatform('aliexpress')} color="#ff4747" icon={<Package />} label="AliExpress" locked={!(isPayingPlan || isAdmin)} />
                            <PlatformBtn active={selectedPlatform === 'tiktok'} onClick={() => setSelectedPlatform('tiktok')} color="#fe2c55" icon={<TikTokIcon size={20} color={selectedPlatform === 'tiktok' ? '#fff' : '#fe2c55'} />} label="TikTok Shop" locked={!(isPayingPlan || isAdmin)} />
                            <PlatformBtn active={selectedPlatform === 'instagram'} onClick={() => setSelectedPlatform('instagram')} color="#e1306c" icon={<InstagramIcon size={24} color={selectedPlatform === 'instagram' ? '#fff' : '#e1306c'} />} label="Instagram" locked={!(isPremiumPlan || isAdmin)} />
                        </div>

                        <div style={cardStyle}>
                            <h3 style={cardTitleStyle}>
                                {selectedPlatform === 'shopee' ? <ShoppingBag size={18} /> : <ShoppingCart size={18} />}
                                Configurações da API
                            </h3>

                            {selectedPlatform === 'shopee' && (
                                /* grid2 garante o espaçamento (gap) entre as colunas */
                                <div className="responsive-grid">
                                    {/* Campo App ID */}
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Shopee App ID</label>
                                        <div style={{ ...inputWrapperStyle, position: 'relative' }}>
                                            <input
                                                style={inputCleanStyle}
                                                value={isEditing.shopeeAppId ? (config.shopeeAppId || '') : maskValue(config.shopeeAppId)}
                                                onChange={(e) => setConfig({ ...config, shopeeAppId: e.target.value })}
                                                disabled={!isEditing.shopeeAppId && config.shopeeAppId}
                                                placeholder="Ex: 762103"
                                            />
                                            {!isEditing.shopeeAppId && config.shopeeAppId && (
                                                <button onClick={() => setIsEditing({ ...isEditing, shopeeAppId: true })} style={editBtnStyleInside}>
                                                    <Edit2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Campo Secret */}
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Shopee Secret</label>
                                        <div style={{ ...inputWrapperStyle, position: 'relative' }}>
                                            <input
                                                style={inputCleanStyle}
                                                type={isEditing.shopeeSecret ? "text" : "password"}
                                                value={config.shopeeSecret || ''}
                                                onChange={(e) => setConfig({ ...config, shopeeSecret: e.target.value })}
                                                disabled={!isEditing.shopeeSecret && config.shopeeSecret}
                                                placeholder="Ex: a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                                            />
                                            {!isEditing.shopeeSecret && config.shopeeSecret && (
                                                <button onClick={() => setIsEditing({ ...isEditing, shopeeSecret: true })} style={editBtnStyleInside}>
                                                    <Edit2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Termo de Busca (Opcional)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.shopeeSearch || ''}
                                            onChange={(e) => setConfig({ ...config, shopeeSearch: e.target.value })}
                                            placeholder="Ex: inverno, verao, casa, mesa, banho..."
                                        />
                                        <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                                            Ao inserir a busca aqui (ex: inverno, verao, casa, mesa, banho, etc.), o robô vai retornar apenas produtos referentes a esta pesquisa. Deixe em branco para trazer ofertas gerais.
                                        </p>
                                    </div>

                                    <div style={{ ...inputGroupStyle, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setConfig({ ...config, shopeeHigherCommissionOnly: !config.shopeeHigherCommissionOnly })}
                                                style={{
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    backgroundColor: config.shopeeHigherCommissionOnly ? '#25d366' : '#cbd5e1',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    transition: '0.3s',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '50%',
                                                    position: 'absolute',
                                                    top: '4px',
                                                    left: config.shopeeHigherCommissionOnly ? '24px' : '4px',
                                                    transition: '0.3s',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }} />
                                            </button>
                                            <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#475569', cursor: 'pointer' }} onClick={() => setConfig({ ...config, shopeeHigherCommissionOnly: !config.shopeeHigherCommissionOnly })}>
                                                Filtrar por Comissão Mínima
                                            </label>
                                        </div>
                                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                                            Filtra e seleciona apenas os produtos com maiores taxas de comissão a partir do valor configurado.
                                        </p>
                                        
                                        {config.shopeeHigherCommissionOnly && (
                                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Porcentagem Mínima de Comissão (%)</label>
                                                <input
                                                    type="number"
                                                    style={{ ...inputStyle, width: '100px' }}
                                                    min="0"
                                                    max="100"
                                                    value={config.shopeeMinCommission !== undefined ? config.shopeeMinCommission : 10}
                                                    onChange={(e) => setConfig({ ...config, shopeeMinCommission: parseInt(e.target.value, 10) || 0 })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedPlatform === 'ml' && (
                                !(isPayingPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="integração com a API do Mercado Livre para conversão e envio de ofertas" />
                                ) : (
                                <div className="responsive-grid">
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>M. Livre User ID (Afiliado)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.mlTag || ''}
                                            onChange={(e) => setConfig({ ...config, mlTag: e.target.value })}
                                            placeholder="Ex: 12345678"
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cookie da Sessão</label>
                                        <textarea
                                            style={{ ...inputStyle, height: '100px' }}
                                            value={config.mlCookie || ''}
                                            onChange={(e) => setConfig({ ...config, mlCookie: e.target.value })}
                                            placeholder="Cole o cookie do Mercado Livre..."
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Termo de Busca (Opcional)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.mlSearch || ''}
                                            onChange={(e) => setConfig({ ...config, mlSearch: e.target.value })}
                                            placeholder="Ex: inverno, verao, casa, mesa, banho..."
                                        />
                                        <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                                            Ao inserir a busca aqui (ex: inverno, verao, casa, mesa, banho, etc.), o robô vai retornar apenas produtos referentes a esta pesquisa. Deixe em branco para trazer ofertas gerais.
                                        </p>
                                    </div>
                                    <div style={{ ...inputGroupStyle, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setConfig({ ...config, mlHigherCommissionOnly: !config.mlHigherCommissionOnly })}
                                                style={{
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    backgroundColor: config.mlHigherCommissionOnly ? '#25d366' : '#cbd5e1',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    transition: '0.3s',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '50%',
                                                    position: 'absolute',
                                                    top: '4px',
                                                    left: config.mlHigherCommissionOnly ? '24px' : '4px',
                                                    transition: '0.3s',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }} />
                                            </button>
                                            <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#475569', cursor: 'pointer' }} onClick={() => setConfig({ ...config, mlHigherCommissionOnly: !config.mlHigherCommissionOnly })}>
                                                Filtrar por Comissão Mínima
                                            </label>
                                        </div>
                                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                                            Filtra e seleciona apenas os produtos com maiores taxas de comissão a partir do valor configurado.
                                        </p>
                                        
                                        {config.mlHigherCommissionOnly && (
                                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Porcentagem Mínima de Comissão (%)</label>
                                                <input
                                                    type="number"
                                                    style={{ ...inputStyle, width: '100px' }}
                                                    min="0"
                                                    max="100"
                                                    value={config.mlMinCommission !== undefined ? config.mlMinCommission : 15}
                                                    onChange={(e) => setConfig({ ...config, mlMinCommission: parseInt(e.target.value, 10) || 0 })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                )
                            )}

                            {selectedPlatform === 'magalu' && (
                                <div className="responsive-grid">
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Nome da Loja Magalu / ID de Afiliado (magazinevoce.com.br/SUA_LOJA)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.magaluTag || ''}
                                            onChange={(e) => setConfig({ ...config, magaluTag: e.target.value })}
                                            placeholder="Ex: magazinemahofertas01"
                                        />
                                    </div>
                                </div>
                            )}

                            {selectedPlatform === 'amazon' && (
                                !(isPayingPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="integração com a API da Amazon para conversão de links e envio de ofertas" />
                                ) : (
                                <div className="responsive-grid">
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Amazon Store Tag (ID de Afiliado)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.amazonTag || ''}
                                            onChange={(e) => setConfig({ ...config, amazonTag: e.target.value })}
                                            placeholder="Ex: mahofertas01-20"
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cookie da Sessão (Amazon)</label>
                                        <textarea
                                            style={{ ...inputStyle, height: '100px' }}
                                            value={config.amazonCookie || ''}
                                            onChange={(e) => setConfig({ ...config, amazonCookie: e.target.value })}
                                            placeholder="Cole o cookie da Amazon autenticado aqui..."
                                        />
                                    </div>
                                </div>
                                )
                            )}

                            {selectedPlatform === 'shein' && (
                                !(isPayingPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="integração com a API da Shein para geração de links de afiliado" />
                                ) : (
                                <div className="responsive-grid">
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Shein UID (Afiliado ID)</label>
                                        <input
                                            style={inputStyle}
                                            value={config.sheinUid || ''}
                                            onChange={(e) => setConfig({ ...config, sheinUid: e.target.value })}
                                            placeholder="Ex: 1234567890"
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Token de Sessão</label>
                                        <input
                                            style={inputStyle}
                                            value={config.sheinToken || ''}
                                            onChange={(e) => setConfig({ ...config, sheinToken: e.target.value })}
                                            placeholder="Token JWT ou hash da sessão"
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Cookie da Sessão (Shein)</label>
                                        <textarea
                                            style={{ ...inputStyle, height: '100px' }}
                                            value={config.sheinCookie || ''}
                                            onChange={(e) => setConfig({ ...config, sheinCookie: e.target.value })}
                                            placeholder="Cole o cookie da Shein autenticado aqui..."
                                        />
                                    </div>
                                </div>
                                )
                            )}

                            {selectedPlatform === 'aliexpress' && (
                                !(isPayingPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="integração com a API do AliExpress para conversão automática de ofertas" />
                                ) : (
                                <div className="responsive-grid">
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>AliExpress App Key</label>
                                        <input
                                            style={inputStyle}
                                            value={config.aliexpressAppKey || ''}
                                            onChange={(e) => setConfig({ ...config, aliexpressAppKey: e.target.value })}
                                            placeholder="Ex: 500123"
                                        />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>AliExpress Secret</label>
                                        <input
                                            style={inputStyle}
                                            type="password"
                                            value={config.aliexpressSecret || ''}
                                            onChange={(e) => setConfig({ ...config, aliexpressSecret: e.target.value })}
                                            placeholder="Ex: a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                                        />
                                    </div>
                                </div>
                                )
                            )}

                            {selectedPlatform === 'tiktok' && (
                                !(isPayingPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="integração com o TikTok Shop para conversão e geração de ofertas" />
                                ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div style={{
                                        backgroundColor: '#eff6ff',
                                        padding: '16px 20px',
                                        borderRadius: '12px',
                                        border: '1px solid #bfdbfe',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '18px' }}>ℹ️</span>
                                            <strong style={{ color: '#1e40af', fontSize: '14px' }}>Envio Manual Ativado (100% Comissionado)</strong>
                                        </div>
                                        <p style={{ margin: 0, color: '#1e3a8a', fontSize: '13px', lineHeight: '1.5' }}>
                                            Para garantir que sua comissão seja sempre atribuída pelo TikTok Shop, o envio funciona através dos links copiados diretamente do aplicativo do TikTok (ex: <code>https://vt.tiktok.com/...</code>).
                                        </p>
                                        <p style={{ margin: 0, color: '#475569', fontSize: '12px' }}>
                                            👉 Basta colar o link no <strong>Conversor de Links</strong> da Dashboard ou enviar no WhatsApp do bot que ele formata o anúncio completo com foto, preço e sua indicação oficial.
                                        </p>
                                    </div>

                                    <div className="responsive-grid">
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>TikTok Tag / Creator ID (Afiliado)</label>
                                            <input
                                                style={inputStyle}
                                                value={config.tiktokTag || config.tiktokPublisherId || ''}
                                                onChange={(e) => setConfig({ ...config, tiktokTag: e.target.value, tiktokPublisherId: e.target.value })}
                                                placeholder="Ex: seu_usuario ou 7432840009596339923"
                                            />
                                            <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>
                                                Seu @ de criador ou ID de afiliado do TikTok Shop.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                )
                            )}

                            {selectedPlatform === 'instagram' && (
                                !(isPremiumPlan || isAdmin) ? (
                                    <PlanBlockedNotice featureName="automação de postagens e respostas no Instagram" requiredPlan="Premium" />
                                ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div>
                                            <strong style={{ fontSize: '14px', color: '#1e293b' }}>Postagem e Respostas no Instagram</strong>
                                            <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>Ativar ou desativar disparos automáticos e respostas de DM no Instagram</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setConfig({ ...config, instagramActive: !config.instagramActive })}
                                            style={{
                                                width: '50px',
                                                height: '26px',
                                                borderRadius: '13px',
                                                backgroundColor: config.instagramActive ? '#e1306c' : '#cbd5e1',
                                                border: 'none',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                transition: '0.3s'
                                            }}
                                        >
                                            <div style={{
                                                width: '18px',
                                                height: '18px',
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                position: 'absolute',
                                                top: '4px',
                                                left: config.instagramActive ? '28px' : '4px',
                                                transition: '0.3s',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }} />
                                        </button>
                                    </div>

                                    {/* Card de Conexão Rápida OAuth */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '16px',
                                        padding: '24px',
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        gap: '16px',
                                        marginBottom: '20px'
                                    }}>
                                        <div style={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '50%',
                                            backgroundColor: '#f5f3ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#8b5cf6'
                                        }}>
                                            <Globe size={32} />
                                        </div>
                                        <div>
                                            <strong style={{ fontSize: '18px', color: '#0f172a', display: 'block' }}>Conexão Rápida em 1 Clique</strong>
                                            <p style={{ fontSize: '13px', color: '#64748b', margin: '6px 0 0 0', maxWidth: '400px' }}>
                                                Conecte a conta do Instagram de forma 100% segura usando a integração oficial do Facebook. Não necessita de códigos ou tokens manuais.
                                            </p>
                                        </div>

                                        {config.instagramBusinessAccountId ? (
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px',
                                                backgroundColor: '#ecfdf5',
                                                border: '1px solid #10b981',
                                                borderRadius: '12px',
                                                padding: '16px 24px',
                                                marginTop: '8px',
                                                width: '100%',
                                                maxWidth: '500px'
                                            }}>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#065f46', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <ShieldCheck size={18} /> Instagram Conectado!
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#047857' }}>
                                                    ID Comercial: {config.instagramBusinessAccountId}
                                                </span>
                                            </div>
                                        ) : null}

                                        <button
                                            type="button"
                                            onClick={handleConnectInstagram}
                                            disabled={loadingInstaAuth}
                                            style={{
                                                backgroundColor: '#1877f2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '12px 24px',
                                                fontSize: '14px',
                                                fontWeight: '800',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                transition: '0.2s',
                                                boxShadow: '0 4px 12px rgba(24, 119, 242, 0.2)'
                                            }}
                                        >
                                            {loadingInstaAuth ? <RefreshCw size={16} className="animate-spin" /> : '🔌'}
                                            {config.instagramBusinessAccountId ? 'Reconectar Instagram / Alterar Conta' : 'Conectar com o Facebook / Instagram'}
                                        </button>

                                    </div>

                                    {/* Acordeão de Configurações Avançadas */}
                                    <div style={{ marginTop: '10px', borderTop: '1px dashed #cbd5e1', paddingTop: '15px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowManualInsta(!showManualInsta)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#64748b',
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '5px 0'
                                            }}
                                        >
                                            {showManualInsta ? '▼ Ocultar Configurações Manuais' : '▶ Mostrar Configurações Avançadas (Manual)'}
                                        </button>

                                        {showManualInsta && (
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '20px',
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '20px'
                                            }}>
                                                <div style={inputGroupStyle}>
                                                    <label style={labelStyle}>Instagram Access Token (Token de Acesso Comercial - Directs)</label>
                                                    <textarea
                                                        style={{ ...inputStyle, height: '80px' }}
                                                        value={config.instagramAccessToken || ''}
                                                        onChange={(e) => setConfig({ ...config, instagramAccessToken: e.target.value })}
                                                        placeholder="Cole o token de longa duração da página gerado na Meta Developers..."
                                                    />
                                                </div>

                                                <div style={inputGroupStyle}>
                                                    <label style={labelStyle}>Instagram Comments Access Token (Token Dedicado para Comentários - Opcional)</label>
                                                    <textarea
                                                        style={{ ...inputStyle, height: '80px' }}
                                                        value={config.instagramCommentsAccessToken || ''}
                                                        onChange={(e) => setConfig({ ...config, instagramCommentsAccessToken: e.target.value })}
                                                        placeholder="Cole o token de comentários (EAAVM...) gerado no Graph API Explorer. Se deixado em branco, o token comercial padrão acima será usado."
                                                    />
                                                </div>

                                                <div className="responsive-grid">
                                                    <div style={inputGroupStyle}>
                                                        <label style={labelStyle}>Instagram Business Account ID</label>
                                                        <input
                                                            style={inputStyle}
                                                            value={config.instagramBusinessAccountId || ''}
                                                            onChange={(e) => setConfig({ ...config, instagramBusinessAccountId: e.target.value })}
                                                            placeholder="Ex: 17841400000000000"
                                                        />
                                                    </div>

                                                    <div style={inputGroupStyle}>
                                                        <label style={labelStyle}>Webhook Verify Token</label>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <input
                                                                style={{ ...inputStyle, flex: 1 }}
                                                                value={config.instagramVerifyToken || ''}
                                                                onChange={(e) => setConfig({ ...config, instagramVerifyToken: e.target.value })}
                                                                placeholder="Ex: anuncios_verify_token"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={gerarVerifyToken}
                                                                style={{
                                                                    padding: '10px 15px',
                                                                    backgroundColor: '#f1f5f9',
                                                                    border: '1px solid #cbd5e1',
                                                                    borderRadius: '8px',
                                                                    fontSize: '13px',
                                                                    fontWeight: 'bold',
                                                                    color: '#475569',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                ⚡ Gerar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={copiarVerifyToken}
                                                                style={{
                                                                    padding: '10px 15px',
                                                                    backgroundColor: '#f1f5f9',
                                                                    border: '1px solid #cbd5e1',
                                                                    borderRadius: '8px',
                                                                    fontSize: '13px',
                                                                    fontWeight: 'bold',
                                                                    color: '#475569',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                📋 Copiar
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="responsive-grid" style={{ marginTop: '20px' }}>
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>Intervalo de Postagem - Feed (Horas)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="24"
                                                style={inputCleanStyle}
                                                value={config.instagramFeedIntervalHours || 2}
                                                onChange={(e) => setConfig({ ...config, instagramFeedIntervalHours: parseInt(e.target.value) || 2 })}
                                                placeholder="Ex: 2"
                                                className="border border-slate-300 rounded-lg p-3 w-full"
                                            />
                                            <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Tempo mínimo de segurança entre posts automáticos no Feed.</span>
                                        </div>

                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>Intervalo de Postagem - Stories (Horas)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="24"
                                                style={inputCleanStyle}
                                                value={config.instagramStoryIntervalHours || 1}
                                                onChange={(e) => setConfig({ ...config, instagramStoryIntervalHours: parseInt(e.target.value) || 1 })}
                                                placeholder="Ex: 1"
                                                className="border border-slate-300 rounded-lg p-3 w-full"
                                            />
                                            <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Tempo mínimo de segurança entre posts automáticos nos Stories.</span>
                                        </div>
                                    </div>
                                    <div style={{ ...inputGroupStyle, marginTop: '20px' }}>
                                        <label style={labelStyle}>Onde Postar as Ofertas?</label>
                                        <select
                                            style={inputCleanStyle}
                                            value={config.instagramPostType || 'both'}
                                            onChange={(e) => setConfig({ ...config, instagramPostType: e.target.value })}
                                            className="border border-slate-300 rounded-lg p-3 w-full"
                                        >
                                            <option value="both">Ambos (Feed e Stories)</option>
                                            <option value="feed">Apenas no Feed</option>
                                            <option value="story">Apenas nos Stories</option>
                                        </select>
                                        <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                            Escolha onde as publicações automáticas do Instagram devem ser postadas.
                                        </span>
                                    </div>
                                </div>
                                )
                            )}

                        </div>
                    </div>
                )}

                {/* ABA TELEGRAM */}
                {mainTab === 'telegram' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{
                            background: 'white',
                            padding: '30px',
                            borderRadius: '15px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
                                <Send size={20} color="#0088cc" />
                                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Configurações do Telegram</h3>
                            </div>

                            {!(isPayingPlan || isAdmin) ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '20px', borderRadius: '12px', textAlign: 'left' }}>
                                    <span style={{ fontSize: '24px' }}>🔒</span>
                                    <div>
                                        <strong style={{ color: '#1e40af', display: 'block', fontSize: '14px', marginBottom: '4px' }}>Recurso Bloqueado no seu Plano</strong>
                                        <span style={{ fontSize: '13px', color: '#1e3a8a' }}>
                                            A integração com bot próprio do Telegram para envio automático de ofertas está disponível apenas para assinantes dos planos <strong>Básico</strong> ou <strong>Premium</strong>. Faça o upgrade de plano na aba de pagamentos para ativar!
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                        Configure seu bot do Telegram para publicar os achadinhos de forma automática ou manual.
                                    </p>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                            <div style={inputGroupStyle}>
                                                <label style={labelStyle}>Telegram Bot Token</label>
                                                <input
                                                    style={inputStyle}
                                                    value={config.telegramToken || ''}
                                                    onChange={(e) => setConfig({ ...config, telegramToken: e.target.value })}
                                                    placeholder="Ex: 123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                                                />
                                                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Token fornecido pelo @BotFather ao criar seu bot.</span>
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <label style={labelStyle}>Telegram Bot Username (@)</label>
                                                <input
                                                    style={inputStyle}
                                                    value={config.telegramBotUsername || ''}
                                                    onChange={(e) => setConfig({ ...config, telegramBotUsername: e.target.value })}
                                                    placeholder="Ex: @seu_bot_ofertas"
                                                />
                                                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Username do bot criado no @BotFather.</span>
                                            </div>
                                        </div>

                                        {(isPremiumPlan || isAdmin) && (
                                            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <strong style={{ fontSize: '14px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <Send size={16} color="#0088cc" /> Auto-Resposta do Telegram (IA)
                                                        </strong>
                                                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                                                            Responder automaticamente a mensagens privadas enviadas para o seu bot do Telegram usando IA.
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfig({ ...config, telegramAutoReply: config.telegramAutoReply !== false ? false : true })}
                                                        style={{
                                                            width: '50px',
                                                            height: '26px',
                                                            borderRadius: '13px',
                                                            backgroundColor: config.telegramAutoReply ? '#0088cc' : '#cbd5e1',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            position: 'relative',
                                                            transition: '0.3s'
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            backgroundColor: '#fff',
                                                            borderRadius: '50%',
                                                            position: 'absolute',
                                                            top: '4px',
                                                            left: config.telegramAutoReply ? '28px' : '4px',
                                                            transition: '0.3s',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                        }} />
                                                    </button>
                                                </div>
                                                {config.telegramAutoReply && (
                                                    <p style={{ fontSize: '11.5px', color: '#64748b', fontStyle: 'italic', margin: 0 }}>
                                                        * O bot do Telegram utilizará as mesmas diretrizes de prompt e comportamento configuradas na aba "WhatsApp".
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                                            <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Send size={18} color="#0088cc" /> Assistente Pessoal (Bot Global)
                                            </h4>
                                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                                                Não quer configurar um bot próprio? Use nosso <strong>Bot Oficial Automático</strong>!
                                                Basta enviar qualquer link de produto para ele, e ele responderá instantaneamente com o link já convertido com a sua comissão e formatado com o seu template!
                                            </p>
                                            
                                            {config.telegramGlobalChatId ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <div style={{ padding: '12px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#065f46', fontWeight: '600', fontSize: '14px' }}>
                                                        <ShieldCheck size={18} /> Conta do Telegram vinculada com sucesso!
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                        <a 
                                                            href={`https://t.me/anunciosappbot`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                padding: '10px 20px',
                                                                background: '#0088cc',
                                                                color: 'white',
                                                                borderRadius: '8px',
                                                                textDecoration: 'none',
                                                                fontWeight: 'bold',
                                                                fontSize: '14px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                                width: 'fit-content',
                                                                boxShadow: '0 2px 4px rgba(0, 136, 204, 0.2)'
                                                            }}
                                                        >
                                                            <Send size={16} /> Abrir o Bot @anunciosappbot
                                                        </a>
                                                        <button 
                                                            onClick={handleUnlinkTelegramGlobal}
                                                            style={{
                                                                padding: '10px 20px',
                                                                background: '#ef4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                fontWeight: 'bold',
                                                                fontSize: '14px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                                cursor: 'pointer',
                                                                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                                                            }}
                                                        >
                                                            Desvincular Telegram
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <a 
                                                    href={`https://t.me/anunciosappbot?start=${config.telegramSyncToken || ''}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        padding: '12px 24px',
                                                        background: '#0088cc',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        textDecoration: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '14px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        width: 'fit-content',
                                                        boxShadow: '0 4px 6px rgba(0, 136, 204, 0.2)'
                                                    }}
                                                    onClick={() => toast.success("Abra o Telegram e clique em 'Start' ou 'Iniciar'!")}
                                                >
                                                    <Send size={18} /> Vincular meu Telegram agora
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                        </div>
                    </div>
                )}
                {/* NOVO: ABA TEMPLATE DE MENSAGEM */}
                {mainTab === 'template' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>

                        <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#25d366', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <MessageCircle size={18} /> 💬 Template Principal (WhatsApp / Telegram)
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                                Modelo de mensagem enviado nos grupos e canais do WhatsApp e Telegram.
                            </p>
                            <textarea
                                style={{ ...inputStyle, height: '120px', fontFamily: 'monospace' }}
                                value={config.templateMensagem || ''}
                                onChange={e => setConfig({ ...config, templateMensagem: e.target.value })}
                                placeholder="Ex: {nome}\n💰 Por: {preco}\n🛒 Link: {link}"
                            />
                        </div>

                        <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#e1306c', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Globe size={18} /> 📸 Legenda do Post no Feed (Instagram)
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                                Legenda da foto publicada no seu Feed do Instagram comercial (sem link público).
                            </p>
                            <textarea
                                style={{ ...inputStyle, height: '120px', fontFamily: 'monospace' }}
                                value={config.templateInstagramLegenda || ''}
                                onChange={e => setConfig({ ...config, templateInstagramLegenda: e.target.value })}
                                placeholder="Ex: 📦 {nome}\n💰 Aproveite: R$ {preco}\n\nComente QUERO abaixo! 👇"
                            />
                        </div>

                        <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#e1306c', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <MessageSquare size={18} /> 💬 Resposta Pública ao Comentário (Instagram)
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                                Resposta pública escrita pelo bot no comentário do cliente (incentiva o engajamento).
                            </p>
                            <textarea
                                style={{ ...inputStyle, height: '80px', fontFamily: 'monospace' }}
                                value={config.templateInstagramComentarioPublico || ''}
                                onChange={e => setConfig({ ...config, templateInstagramComentarioPublico: e.target.value })}
                                placeholder="Ex: Olá @{username}! Enviei o link no seu Direct!"
                            />
                            <div style={{ marginTop: '8px', fontSize: '11px', color: '#1e40af' }}>
                                <strong>Variável específica:</strong> <code>{'{username}'}</code> (insere o @ do cliente)
                            </div>
                        </div>

                        <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#e1306c', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Send size={18} /> 📸 Resposta DM - Feed Instagram
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                                Modelo de direct message (DM) enviado automaticamente ao receber comentários com "QUERO" em posts do Feed.
                            </p>
                            <textarea
                                style={{ ...inputStyle, height: '120px', fontFamily: 'monospace' }}
                                value={config.templateInstagramFeed || ''}
                                onChange={e => setConfig({ ...config, templateInstagramFeed: e.target.value })}
                                placeholder="Ex: Olá! Aqui está seu link...\n📦 {nome}\n🛒 {link}"
                            />
                        </div>

                        {/* <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#e1306c', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Video size={18} /> 🎬 Resposta DM - Stories Instagram
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                                Modelo de direct message (DM) enviado automaticamente ao receber respostas/reações a um Story com "QUERO".
                            </p>
                            <textarea
                                style={{ ...inputStyle, height: '120px', fontFamily: 'monospace' }}
                                value={config.templateInstagramStory || ''}
                                onChange={e => setConfig({ ...config, templateInstagramStory: e.target.value })}
                                placeholder="Ex: Olá! Aqui está seu link...\n📦 {nome}\n🛒 {link}"
                            />
                        </div> */}

                        <div style={{ padding: '15px', background: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
                            <strong>Variáveis de produto disponíveis:</strong><br />
                            <code>{'{nome}'}</code>, <code>{'{preco}'}</code>, <code>{'{precoAntigo}'}</code>, <code>{'{link}'}</code>, <code>{'{escassez}'}</code>, <code>{'{loja}'}</code>
                        </div>
                    </div>
                )}

                {/* NOVO: ABA VITRINE PÚBLICA */}
                {mainTab === 'vitrine' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <div style={cardStyle}>
                            <h3 style={{ ...cardTitleStyle, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Store size={18} /> Configurações da Vitrine Pública
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
                                A Vitrine Pública lista automaticamente todas as suas ofertas publicadas, organizadas e com seus links de afiliados.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {(config.instagramUsername || username) && (
                                    <div style={{ 
                                        padding: '15px', 
                                        backgroundColor: '#ecfdf5', 
                                        border: '1px dashed #34d399', 
                                        borderRadius: '12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        gap: '8px'
                                    }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#047857', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            🔗 SUA VITRINE AUTOMÁTICA DE LINKS:
                                        </span>
                                        <a 
                                            href={`${window.location.origin}/vitrine/${config.instagramUsername || username}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            style={{ fontSize: '15px', fontWeight: '700', color: '#059669', textDecoration: 'underline', wordBreak: 'break-all' }}
                                        >
                                            {`${window.location.origin.replace('https://', '').replace('http://', '')}/vitrine/${config.instagramUsername || username}`}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${window.location.origin}/vitrine/${config.instagramUsername || username}`);
                                                toast.success("Link da Vitrine copiado!");
                                            }}
                                            style={{
                                                padding: '6px 16px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: '#059669',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: '0.2s'
                                            }}
                                        >
                                            Copiar Link da Vitrine
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <label style={labelStyle}>
                                        Nome de Usuário / Slug da Vitrine
                                    </label>
                                    <input 
                                        type="text" 
                                        placeholder="Ex: mahrodriguesofertas" 
                                        value={config.instagramUsername || ''} 
                                        onChange={(e) => setConfig({ ...config, instagramUsername: e.target.value.replace('@', '').trim() })}
                                        style={inputStyle}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                                        Define o endereço da sua vitrine (ex: anunciosapp.shop/vitrine/nome_usuario). Caso deixe em branco, será utilizado seu nome de usuário de cadastro do sistema.
                                    </span>
                                </div>

                                <div>
                                    <label style={labelStyle}>
                                        URL da Foto de Perfil da Vitrine
                                    </label>
                                    <input 
                                        type="url" 
                                        placeholder="https://..." 
                                        value={config.instagramProfilePictureUrl || ''} 
                                        onChange={(e) => setConfig({ ...config, instagramProfilePictureUrl: e.target.value.trim() })}
                                        style={inputStyle}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                                        Defina um link de imagem (PNG/JPG) para personalizar a foto de perfil da sua vitrine.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {mainTab === 'whatsapp' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', width: '100%' }}>
                            {renderWhatsappBox('me', 'Seu WhatsApp (Remetente / Automação)', whatsStatusMe, qrCodeMe, false)}
                        </div>
                    </div>
                )}


            </div>
        </div>
    );
};

// Componentes Auxiliares de Estilo
const TabBtn = ({ active, onClick, icon, label }) => (
    <button onClick={onClick} style={{
        padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
        backgroundColor: active ? '#25d366' : '#fff', color: active ? '#fff' : '#64748b',
        display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: '0.2s'
    }}>
        {icon} {label}
    </button>
);

const PlanBlockedNotice = ({ featureName, requiredPlan = "Básico ou Premium" }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '20px', borderRadius: '12px', textAlign: 'left', width: '100%', boxSizing: 'border-box' }}>
        <span style={{ fontSize: '24px' }}>🔒</span>
        <div>
            <strong style={{ color: '#1e40af', display: 'block', fontSize: '14px', marginBottom: '4px' }}>Recurso Bloqueado no seu Plano</strong>
            <span style={{ fontSize: '13px', color: '#1e3a8a' }}>
                A {featureName} está disponível apenas para assinantes dos planos <strong>{requiredPlan}</strong>. Faça o upgrade de plano na aba de pagamentos para ativar!
            </span>
        </div>
    </div>
);

const PlatformBtn = ({ active, onClick, color, icon, label, textColor = "#fff", locked = false }) => (
    <button onClick={onClick} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
        padding: '15px', borderRadius: '12px', cursor: 'pointer', width: '100px',
        border: active ? `3px solid ${color}` : '1px solid #e2e8f0',
        backgroundColor: active ? color : '#fff', color: active ? textColor : '#64748b',
        position: 'relative',
        transition: 'all 0.2s ease'
    }}>
        {locked && (
            <span style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                fontSize: '10px',
                lineHeight: 1,
                padding: '2px 4px',
                borderRadius: '6px',
                backgroundColor: active ? 'rgba(0,0,0,0.2)' : '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }} title="Recurso Bloqueado no seu plano">
                🔒
            </span>
        )}
        {icon} <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{label}</span>
    </button>
);

// Estilos Reutilizados
const labelStyle = { display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold', color: '#475569' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' };
const cardStyle = {
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0'
};

const cardTitleStyle = {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '15px',
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: '10px'
};

const inputGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
};

const inputWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    paddingRight: '12px',
    transition: '0.2s focus-within',
};

const inputCleanStyle = {
    width: '100%',
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    outline: 'none',
    fontSize: '15px',
    fontWeight: '500'
};

const editBtnStyleInside = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
    color: '#64748b',
    zIndex: 10
};
export default ConfigPage;